import React from "react";
import { Bar } from "react-chartjs-2";
import { Card, CardBody, CardTitle, Col, Input, Table } from "reactstrap";
import { useTranslation } from "react-i18next";
import i18n from "i18n";

export const GOLD = "#b8962e";
const localeOf = () => (i18n.language === "en" ? "en-GB" : "it-IT");

export const eur = (n) => {
  const text = Number(n || 0).toLocaleString(localeOf(), { minimumFractionDigits: 2, maximumFractionDigits: 2 });
  return i18n.language === "en" ? `€${text}` : `${text} €`;
};

export const PeriodSelect = ({ days, onChange }) => {
  const { t } = useTranslation("common");
  return (
    <Input type="select" value={days} onChange={(e) => onChange(Number(e.target.value))} style={{ width: 200 }}>
      <option value={7}>{t("stats.last7")}</option>
      <option value={30}>{t("stats.last30")}</option>
      <option value={90}>{t("stats.last90")}</option>
      <option value={365}>{t("stats.lastYear")}</option>
    </Input>
  );
};

// variazione rispetto al periodo precedente
export const Delta = ({ now, prev }) => {
  const { t } = useTranslation("common");
  if (!prev) return now ? <span className="small text-muted">{t("stats.new")}</span> : null;
  const p = Math.round(((now - prev) / prev) * 100);
  return <span className="small" style={{ color: p >= 0 ? "#2e7d32" : "#c62828" }}>{p >= 0 ? "▲" : "▼"} {Math.abs(p)}%</span>;
};

export const Kpi = ({ label, value, sub, md = 3 }) => (
  <Col md={md} sm="6" className="mb-3">
    <Card className="h-100 mb-0">
      <CardBody>
        <div className="text-muted small text-uppercase">{label}</div>
        <div style={{ fontSize: 28, fontWeight: 600 }}>{value}</div>
        {sub && <div>{sub}</div>}
      </CardBody>
    </Card>
  </Col>
);

export const BarCard = ({ title, labels, data, label, money, height = 220 }) => {
  const { t } = useTranslation("common");
  return (
  <Card>
    <CardBody>
      <CardTitle tag="h5">{title}</CardTitle>
      {data.some((v) => v) ? (
        <div style={{ height }}>
          <Bar
            data={{ labels, datasets: [{ label, data, backgroundColor: GOLD, borderRadius: 4 }] }}
            options={{
              maintainAspectRatio: false,
              animation: { duration: 300 },
              plugins: { legend: { display: false }, tooltip: { callbacks: money ? { label: (c) => eur(c.raw) } : {} } },
              scales: { y: { beginAtZero: true, ticks: { precision: 0 } }, x: { grid: { display: false } } },
            }}
          />
        </div>
      ) : (
        <p className="text-muted mb-0">{t("stats.noData")}</p>
      )}
    </CardBody>
  </Card>
  );
};

export const TableCard = ({ title, cols, rows, empty }) => {
  const { t } = useTranslation("common");
  return (
  <Card>
    <CardBody>
      <CardTitle tag="h5">{title}</CardTitle>
      {rows.length ? (
        <Table size="sm" className="mb-0">
          <thead><tr>{cols.map(([k, l, right]) => <th key={k} className={right ? "text-right" : ""}>{l}</th>)}</tr></thead>
          <tbody>
            {rows.map((r, i) => (
              <tr key={i}>{cols.map(([k, , right, fmt]) => <td key={k} className={right ? "text-right text-nowrap" : ""}>{fmt ? fmt(r[k], r) : r[k]}</td>)}</tr>
            ))}
          </tbody>
        </Table>
      ) : (
        <p className="text-muted mb-0">{empty ?? t("stats.noData")}</p>
      )}
    </CardBody>
  </Card>
  );
};

export const dayLabel = (iso) => {
  const d = new Date(`${iso}T12:00:00`);
  return d.toLocaleDateString(localeOf(), { day: "2-digit", month: "2-digit" });
};

// Giorni della settimana abbreviati (lunedi' = 0): letti al momento dell'uso, seguono la lingua corrente.
// E' un vero array: ogni posizione e' un accessor che traduce al momento della lettura.
export const WEEKDAYS = (() => {
  const days = ["mon", "tue", "wed", "thu", "fri", "sat", "sun"];
  const list = new Array(days.length);
  days.forEach((day, i) => {
    Object.defineProperty(list, i, {
      enumerable: true,
      get: () => i18n.t(`common:weekdays.${day}`),
    });
  });
  return list;
})();

// CSV semplice da righe
export const downloadCsv = (name, header, rows) => {
  const esc = (v) => `"${String(v ?? "").replace(/"/g, '""')}"`;
  const text = "﻿" + [header, ...rows].map((r) => r.map(esc).join(";")).join("\n");
  const url = URL.createObjectURL(new Blob([text], { type: "text/csv;charset=utf-8" }));
  const a = document.createElement("a");
  a.href = url;
  a.download = name;
  document.body.appendChild(a);
  a.click();
  a.remove();
  URL.revokeObjectURL(url);
};
